import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../../components/Icon";
import { StatusBadge, PriorityBadge } from "../../components/Badges";
import LoadingBlock from "../../components/LoadingBlock";
import Breadcrumbs from "../../components/Breadcrumbs";
import { useApi } from "../../hooks/useApi";
import { useRefresh } from "../../contexts/RefreshContext";
import {
  getMyAssignedTicket,
  sendMyAssignedTicketMessage,
  updateMyAssignedTicketStatus,
} from "../../api/myAssignedTicketService";
import { formatDate, fromNow, ticketNumber, initials } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";

export default function MyAssignedTicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);

  const { refreshKey } = useRefresh();
  const ticket = useApi(() => getMyAssignedTicket(id), [id, refreshKey]);
  const t = ticket.data?.data;

  const send = async () => {
    if (!reply.trim()) return;
    setSaving(true);
    try {
      await sendMyAssignedTicketMessage(id, reply);
      setReply("");
      ticket.reload();
    } catch (err) { showApiError(err); }
    finally { setSaving(false); }
  };

  const setStatus = async (status) => {
    try {
      await updateMyAssignedTicketStatus(id, status);
      showToast("success", "Status updated");
      ticket.reload();
    } catch (err) { showApiError(err); }
  };

  if (ticket.loading || !t) return <LoadingBlock rows={3} height={80} />;
  const messages = t.messages || [];

  return (
    <>
      <Breadcrumbs
        items={[
          { label: "Assigned Tickets", to: "/me/tickets" },
          { label: ticketNumber(t.id) },
        ]}
      />
      <div className="page-head">
        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginLeft: -8, marginBottom: 8 }}>
            <Icon name="chevron-left" size={14} /> Back
          </button>
          <h1 className="page-title">{t.subject}</h1>
          <div className="page-sub row gap-sm" style={{ marginTop: 4 }}>
            <span className="t-mono">{ticketNumber(t.id)}</span>
            <span>·</span>
            <span>{formatDate(t.created_at)}</span>
            <span>·</span>
            <span>{t.customer?.name}</span>
          </div>
        </div>
        <div className="page-actions">
          <select
            className="select"
            value={["in_progress", "resolved", "closed"].includes(t.status) ? t.status : "in_progress"}
            onChange={(e) => setStatus(e.target.value)}
            style={{ width: 160 }}
          >
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <PriorityBadge priority={t.priority} />
          <StatusBadge status={t.status} />
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="ticket-thread">
            {t.description && (
              <div className="bubble customer">
                <div className="avatar avatar-sm">{initials(t.customer?.name)}</div>
                <div>
                  <div className="bubble-content">{t.description}</div>
                  <div className="bubble-meta">{formatDate(t.created_at)}</div>
                </div>
              </div>
            )}
            {messages.map((m) => {
              const r = m.sender?.role;
              const right = r === "user" || r === "admin";
              const cls = r === "admin" ? "admin" : r === "customer" ? "customer" : "employee";
              return (
                <div key={m.id} className={`bubble ${cls} ${right ? "right" : ""}`}>
                  <div className="avatar avatar-sm">{initials(m.sender?.name)}</div>
                  <div>
                    <div className="bubble-content">{m.message}</div>
                    <div className="bubble-meta" style={right ? { justifyContent: "flex-end" } : null}>
                      <span>{m.sender?.name}</span><span>·</span><span>{fromNow(m.created_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 24, padding: 14, background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 10 }}>
            <textarea
              className="textarea"
              placeholder="Reply…"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              style={{ background: "white", minHeight: 80 }}
            />
            <div className="row" style={{ marginTop: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-primary btn-sm" disabled={!reply.trim() || saving} onClick={send}>
                <Icon name="send" size={12} /> Send reply
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
